import React from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';

import {
    L1CategoryId,
    L2CategoryId,
    getL1CategoryIdBySeoName,
    getL2CategoryIdBySeoName,
} from '@gumtree/shared/src/util/category-mapping';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { SellerType } from '@gumtree/shared/src/types/advertising/partnership';
import accountIdsToBeBlacklisted from '@gumtree/shared/src/partnerships/motors-finance-blacklist';

import { getShortTimeSince } from '../utils/time-service';

import {
    FullTime,
    H2,
    LocationInline,
    PriceStyle,
    ShortTime,
    TagsContainer,
    TimeWrapperStyle,
    VatStyle,
} from './listing-tile.style';
import { colors } from '../base/theme';
import { qaAttribute } from '../utils/qa-service';
import { LocationAtMiddleWrapper } from './location-wrapper/location-at-middle-wrapper';
import { InfoWrapper } from './info-wrapper/info-wrapper';
import { TileHeaderWrapper } from './title-row-wrapper/title-row-wrapper';
import CarMonthlyPrice from './car-monthly-price/car-monthly-price';
import DeliveryTag from './delivery-tag';

export interface Props {
    title: string;
    isGridViewOnDesktop?: boolean;
    attributesOrDescription: {
        isAttributes: boolean;
        render: () => React.ReactElement | undefined;
    };
    favouriteToggle: React.ReactElement | undefined;
    location: string;
    timeSincePosted?: string;
    price: string;
    sellerId: number;
    l1CategoryId: L1CategoryId;
    l2CategoryId?: L2CategoryId;
    vat?: string;
    supportShipping?: boolean;
    showDate?: boolean;
    sellerType?: SellerType | null;
    accountId?: number;
    setOpenHPModal?: (open: boolean) => void;
}

const TileInfoGeneral = ({
    isGridViewOnDesktop,
    title,
    attributesOrDescription,
    location,
    timeSincePosted = '',
    price,
    vat,
    l1CategoryId,
    l2CategoryId,
    favouriteToggle,
    supportShipping,
    showDate = true,
    sellerType,
    accountId,
    setOpenHPModal,
}: Props) => {
    const hasPrice = Boolean(price);
    const isLocationInline = !hasPrice;

    const isDescriptionInBarkColour =
        l1CategoryId === getL1CategoryIdBySeoName('business-services') ||
        l1CategoryId === getL1CategoryIdBySeoName('community');

    const showPostedDate =
        l1CategoryId !== getL1CategoryIdBySeoName('business-services') && showDate;

    const isL2CarsForSale =
        l1CategoryId === getL1CategoryIdBySeoName('cars-vans-motorbikes') &&
        (l2CategoryId === getL2CategoryIdBySeoName('cars') ||
            l2CategoryId === getL2CategoryIdBySeoName('vans') ||
            l2CategoryId === getL2CategoryIdBySeoName('motorbikes-scooters'));

    const isBlacklistSeller = accountIdsToBeBlacklisted.includes(accountId!);

    const showMonthlyPrice =
        useFeatureValue(GrowthBookFeature.CARMONEY_MONTHLY_PRICE_SRP, 'A') === 'B' &&
        isL2CarsForSale &&
        sellerType === 'Trade' &&
        !isBlacklistSeller;

    const render = () => (
        <div
            style={isDescriptionInBarkColour ? { color: colors.bark60 } : {}}
            suppressHydrationWarning
            {...qaAttribute('tile-description')}
        >
            {attributesOrDescription.render()}
        </div>
    );

    return (
        <>
            <TileHeaderWrapper>
                <H2 isGridView={isGridViewOnDesktop} {...qaAttribute('tile-title')}>
                    {title}
                </H2>
                {favouriteToggle}
            </TileHeaderWrapper>
            <>{render()}</>
            {!isLocationInline && (
                <LocationAtMiddleWrapper
                    isGridView={isGridViewOnDesktop}
                    {...qaAttribute('tile-location')}
                >
                    {location}
                </LocationAtMiddleWrapper>
            )}
            <InfoWrapper isL2CarsForSale={isL2CarsForSale}>
                {isLocationInline ? (
                    <LocationInline
                        {...qaAttribute('tile-location')}
                        isGridView={isGridViewOnDesktop}
                    >
                        {location}
                    </LocationInline>
                ) : (
                    <div>
                        <div css={PriceStyle} data-testid="price" {...qaAttribute('tile-price')}>
                            {price}
                            {vat && <span css={VatStyle}> ({vat})</span>}
                        </div>
                        {showMonthlyPrice && (
                            <CarMonthlyPrice price={price} setOpenHPModal={setOpenHPModal} />
                        )}
                    </div>
                )}
                <TagsContainer>
                    {supportShipping && <DeliveryTag />}
                    {showPostedDate && (
                        <div css={TimeWrapperStyle} {...qaAttribute('tile-datePosted')}>
                            {!isLocationInline ? (
                                timeSincePosted
                            ) : (
                                <>
                                    <ShortTime>{getShortTimeSince(timeSincePosted)}</ShortTime>
                                    <FullTime>{timeSincePosted}</FullTime>
                                </>
                            )}
                        </div>
                    )}
                </TagsContainer>
            </InfoWrapper>
        </>
    );
};

export default TileInfoGeneral;
